import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon, type IconName } from '../ui/Icon';
import { Bouton, cn, useToasts } from '../ui/kit';
import { Media } from '../ui/Media';
import { ProductCard } from '../components/ProductCard';
import { useBoutique } from '../state/boutique';
import { clsBouton } from './partages';
import { BOUTIQUES, COMMANDES, COMPTE, PRODUITS, REGLAGES, type Commande } from '../data/mock-data';
import { eur, kg, poidsParDefaut, produits, produitsSignature } from '../data/repository';

/**
 * ESPACE CLIENT — un tableau de bord, pas un formulaire administratif.
 *
 * La critique portait sur deux choses : on s'y perd, et il n'y a rien à y faire.
 * Réponse : cinq entrées maximum, un contenu utile à chaque entrée, des cartes
 * aérées, et sur téléphone une navigation par onglets qui défile horizontalement
 * (jamais un menu enterré).
 */
type Vue = 'apercu' | 'commandes' | 'suivi' | 'favoris' | 'adresses' | 'preferences';

const ONGLETS: Array<{ cle: Vue; libelle: string; icone: IconName }> = [
  { cle: 'apercu', libelle: 'Vue d’ensemble', icone: 'home' },
  { cle: 'commandes', libelle: 'Mes commandes', icone: 'package' },
  { cle: 'suivi', libelle: 'Suivi', icone: 'truck' },
  { cle: 'favoris', libelle: 'Favoris', icone: 'heart' },
  { cle: 'adresses', libelle: 'Adresses', icone: 'pin' },
  { cle: 'preferences', libelle: 'Préférences', icone: 'settings' },
];

const STATUTS: Record<string, { libelle: string; ton: string }> = {
  NOUVELLE: { libelle: 'Reçue', ton: 'bg-info-wash text-info' },
  CONFIRMEE: { libelle: 'Confirmée', ton: 'bg-info-wash text-info' },
  EN_PREPARATION: { libelle: 'En préparation', ton: 'bg-alert-wash text-alert' },
  POIDS_SAISI: { libelle: 'Pesée faite', ton: 'bg-brass-wash text-brass-ink' },
  PRETE: { libelle: 'Prête au retrait', ton: 'bg-success-wash text-success' },
  EXPEDIEE: { libelle: 'Expédiée', ton: 'bg-info-wash text-info' },
  CLOTUREE: { libelle: 'Retirée', ton: 'bg-success-wash text-success' },
  NON_RETIREE: { libelle: 'Non retirée', ton: 'bg-danger-wash text-danger' },
  PAIEMENT_ECHEC: { libelle: 'Paiement à régler', ton: 'bg-danger-wash text-danger' },
  ANNULEE: { libelle: 'Annulée', ton: 'bg-linen text-muted' },
};
const statutDe = (c: Commande) => STATUTS[c.statut] ?? { libelle: c.statutLisible, ton: 'bg-linen text-muted' };

export function Compte() {
  const { numero } = useParams<{ numero?: string }>();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const vueDemandee = params.get('vue');
  const vueInitiale: Vue = numero ? 'commandes'
    : (['apercu', 'commandes', 'suivi', 'favoris', 'adresses', 'preferences'] as Vue[]).includes(vueDemandee as Vue)
      ? (vueDemandee as Vue) : 'apercu';
  const [vue, setVue] = useState<Vue>(vueInitiale);
  const { confort, basculerConfort, favoris, ajouter, ouvrirPanier } = useBoutique();
  const { annoncer } = useToasts();
  const mesCommandes = COMMANDES.filter(c => c.clientNom === `${COMPTE.prenom} ${COMPTE.nom}`);
  const prochaine = mesCommandes[0] ?? COMMANDES[0];
  const favorisProduits = PRODUITS.filter(p => favoris.includes(p.id));
  const suggestions = favorisProduits.length ? favorisProduits : produitsSignature(2);

  /* Un lien reçu par SMS ouvre directement le détail : `/mon-compte/commandes/CMD-…`. */
  useEffect(() => { if (numero) setVue('commandes'); }, [numero]);
  useEffect(() => { if (vueDemandee && !numero) setVue(vueInitiale); /* lien profond « ?vue=favoris » */ }, [vueDemandee, numero]);
  const commandeOuverte = numero ? COMMANDES.find(c => c.numero === numero) : undefined;

  /* Suivi sans compte : le numéro de commande suffit, le téléphone ou le nom aussi.
     C'est la promesse de la page — on ne demande ni mot de passe ni e-mail. */
  const [recherche, setRecherche] = useState('');
  const [suiviNumero, setSuiviNumero] = useState<string | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const suivie = (suiviNumero ? COMMANDES.find(c => c.numero === suiviNumero) : undefined) ?? prochaine;

  const chercher = (e: FormEvent) => {
    e.preventDefault();
    const t = recherche.trim().toLowerCase();
    if (t.length < 3) { setIntrouvable(false); setSuiviNumero(null); return; }
    const chiffres = t.replace(/\D/g, '');
    const trouvee = COMMANDES.find(c =>
      c.numero.toLowerCase().includes(t)
      || (chiffres.length >= 4 && (c.clientTelephone || '').replace(/\D/g, '').includes(chiffres))
      || c.clientNom.toLowerCase().includes(t));
    setSuiviNumero(trouvee?.numero ?? null);
    setIntrouvable(!trouvee);
  };

  const commander = (c: Commande) => {
    const parNom = new Map(produits().map(p => [p.nom, p]));
    let ajouts = 0;
    for (const l of c.lignes) {
      const p = parNom.get(l.nom);
      if (!p) continue;
      ajouter(p, {
        poidsG: l.poidsEstimeG ?? poidsParDefaut(p),
        quantite: l.quantite,
      });
      ajouts += 1;
    }
    if (ajouts > 0) {
      annoncer({
        texte: `${c.numero} ajoutée au panier — ${ajouts} ligne${ajouts !== 1 ? 's' : ''}.`,
        action: { libelle: 'Voir le panier', faire: () => ouvrirPanier(true) },
      });
    } else {
      annoncer({ texte: 'Ces pièces ne sont plus au catalogue — le comptoir peut les retrouver.' });
    }
  };

  return (
    <main id="contenu" className="mx-auto max-w-[1180px] px-5 py-8 sm:px-6">
      {/* ------------------------------------------------------------- en-tête */}
      <div className="flex flex-col gap-5 rounded-[24px] bg-ink p-6 text-lin-50 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="flex items-center gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-lin-50/10 font-display text-xl text-brass-soft">
            {COMPTE.prenom.slice(0, 1)}{COMPTE.nom.slice(0, 1)}
          </span>
          <div>
            <p className="text-[0.6875rem] uppercase tracking-[0.16em] text-brass-soft">Bonjour</p>
            <h1 className="mt-0.5 font-display text-2xl text-lin-50 sm:text-3xl">{COMPTE.prenom} {COMPTE.nom}</h1>
            <p className="mt-1 text-sm text-lin-50/60">Client depuis {COMPTE.depuis} · {COMPTE.email}</p>
          </div>
        </div>
        <div className="rounded-2xl bg-lin-50/[0.07] p-4 sm:min-w-[220px]">
          <div className="flex items-center justify-between text-sm">
            <span className="text-lin-50/70">Fidélité {COMPTE.fidelite.palier}</span>
            <span className="font-semibold text-brass-soft">{COMPTE.fidelite.points} pts</span>
          </div>
          <div className="meter mt-2 bg-lin-50/15">
            <div className="meter__fill" style={{ width: `${Math.min(100, (COMPTE.fidelite.points / COMPTE.fidelite.prochain) * 100)}%` }} />
          </div>
          <p className="mt-2 text-xs text-lin-50/55">{COMPTE.fidelite.prochain - COMPTE.fidelite.points} points avant le palier suivant</p>
        </div>
      </div>

      {/* -------------------------------------------------------------- onglets */}
      <div className="mt-6 lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">
        <nav aria-label="Espace client" className="-mx-5 overflow-x-auto px-5 no-scrollbar lg:mx-0 lg:px-0">
          <ul className="flex gap-1.5 lg:flex-col">
            {ONGLETS.map(o => (
              <li key={o.cle} className="shrink-0 lg:shrink">
                <button type="button" onClick={() => setVue(o.cle)} aria-current={vue === o.cle ? 'page' : undefined}
                  className={cn('flex w-full items-center gap-2.5 whitespace-nowrap rounded-menu px-4 py-3 text-sm transition',
                    vue === o.cle ? 'bg-ink text-lin-50' : 'bg-surface text-ink-soft hover:bg-linen')}>
                  <Icon name={o.icone} size={17} />
                  {o.libelle}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-6 min-w-0 lg:mt-0">
          {/* ------------------------------------------------------- APERÇU */}
          {vue === 'apercu' && (
            <div className="space-y-6">
              <section className="rounded-[22px] bg-surface p-5 shadow-card sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="eyebrow">Prochaine commande</p>
                    <p className="mt-1 font-display text-xl">{prochaine.numero}</p>
                  </div>
                  <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', statutDe(prochaine).ton)}>
                    {statutDe(prochaine).libelle}
                  </span>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <Info icone="calendar" titre="Créneau" valeur={prochaine.creneau} />
                  <Info icone={prochaine.mode === 'PICKUP' ? 'store' : 'truck'} titre="Mode" valeur={prochaine.modeLisible} />
                  <Info icone="scale" titre="Montant" valeur={eur(prochaine.total)} />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Bouton taille="sm" onClick={() => setVue('suivi')} icone="truck">Suivre ma commande</Bouton>
                  <Bouton taille="sm" variante="quiet" onClick={() => setVue('commandes')}>Toutes mes commandes</Bouton>
                </div>
              </section>

              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { k: 'Commandes au comptoir', v: String(mesCommandes.length) },
                  { k: 'Panier moyen', v: eur(mesCommandes.reduce((n, c) => n + c.total, 0) / Math.max(1, mesCommandes.length)) },
                  { k: 'Adresse par défaut', v: COMPTE.adresses[0].ville },
                ].map(s => (
                  <div key={s.k} className="rounded-[18px] bg-surface p-4 shadow-soft">
                    <p className="text-xs text-muted">{s.k}</p>
                    <p className="mt-1 font-display text-xl">{s.v}</p>
                  </div>
                ))}
              </div>

              <section>
                <div className="flex items-end justify-between">
                  <h2 className="font-display text-xl">{favorisProduits.length ? 'Vos favoris' : 'À découvrir'}</h2>
                  <Link to="/boutique" className="text-sm font-semibold underline underline-offset-4">Le comptoir</Link>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {suggestions.map(p => (
                    <div key={p.id} className="flex items-center gap-3 rounded-[18px] bg-surface p-3 shadow-soft">
                      <Media src={p.vignette} alt={p.nom} ratio="aspect-square" className="h-16 w-16 shrink-0 rounded-xl" monogramme={p.nom.slice(0, 1)} />
                      <div className="min-w-0 flex-1">
                        <Link to={`/produit/${p.slug}`} className="block truncate font-medium hover:text-wine">{p.nom}</Link>
                        <p className="text-xs text-muted">{p.prixKg ? `${eur(p.prixKg)}/kg` : eur(p.prixFixe ?? 0)}</p>
                      </div>
                      <button type="button" onClick={() => { ajouter(p); annoncer({ texte: `${p.nom} ajouté au panier.` }); }}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-linen text-ink-soft transition hover:bg-ink hover:text-lin-50"
                        aria-label={`Ajouter ${p.nom}`}>
                        <Icon name="plus" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {/* ---------------------------------------------------- COMMANDES */}
          {vue === 'commandes' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-display text-2xl">Mes commandes</h2>
                {numero && (
                  <button type="button" onClick={() => nav('/mon-compte')}
                    className="text-sm font-medium text-ink-soft underline underline-offset-4 hover:text-ink">
                    Toutes mes commandes
                  </button>
                )}
              </div>

              {numero && !commandeOuverte && (
                <p className="rounded-2xl bg-surface p-5 text-sm text-ink-soft shadow-soft">
                  Aucune commande ne porte ce numéro. Vérifiez le code reçu par SMS — ou appelez le comptoir,
                  nous la retrouvons avec votre nom.
                </p>
              )}

              {commandeOuverte && <DetailCommande c={commandeOuverte} onCommander={() => commander(commandeOuverte)} />}
              {!commandeOuverte && mesCommandes.map(c => (
                <article key={c.numero} className="rounded-[20px] bg-surface p-5 shadow-soft">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-display text-lg">{c.numero}</p>
                      <p className="text-xs text-muted">{c.date} · {c.modeLisible}</p>
                    </div>
                    <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', statutDe(c).ton)}>{c.statutLisible}</span>
                  </div>
                  <ul className="mt-3 space-y-1 text-sm text-ink-soft">
                    {c.lignes.map(l => (
                      <li key={l.nom} className="flex justify-between gap-3">
                        <span>{l.nom} <span className="text-muted">× {l.quantite}</span></span>
                        <span className="tabular-nums">{eur(l.prix)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-linen-deep/70 pt-3">
                    <p className="text-sm text-muted">{c.creneau}</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-semibold tabular-nums">{eur(c.total)}</span>
                      <Link to={`/mon-compte/commandes/${c.numero}`}
                        className="text-sm font-semibold text-wine underline underline-offset-4">Détail & pesée</Link>
                      <button type="button" onClick={() => commander(c)}
                        className="text-sm font-medium text-ink-soft underline underline-offset-4 hover:text-ink">Recommander</button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* -------------------------------------------------------- SUIVI */}
          {vue === 'suivi' && (
            <div>
              <h2 className="font-display text-2xl">Où en est ma commande&nbsp;?</h2>
              <p className="mt-1 text-sm text-muted">
                Indiquez le numéro reçu par SMS (il commence par CMD-2026-). Votre téléphone ou votre nom
                fonctionnent aussi — aucun compte n’est nécessaire.
              </p>

              <form onSubmit={chercher} role="search" className="mt-4 flex flex-col gap-2 sm:flex-row">
                <div className="flex-1">
                  <label htmlFor="suivi-q" className="sr-only">Numéro de commande, téléphone ou nom</label>
                  <input id="suivi-q" type="search" value={recherche}
                    onChange={e => { setRecherche(e.target.value); setIntrouvable(false); }}
                    placeholder="CMD-2026-1048 · 06 12 34 56 78 · Nadia"
                    className="input h-12" autoComplete="off" />
                </div>
                <Bouton type="submit" variante="ink" className="sm:w-auto">Rechercher</Bouton>
              </form>
              {introuvable && (
                <p className="mt-3 rounded-xl bg-danger-wash/60 px-4 py-3 text-sm text-danger" role="status">
                  Aucune commande ne correspond. Vérifiez le numéro reçu par SMS, ou appelez le comptoir au{' '}
                  {REGLAGES.marque.telephone}.
                </p>
              )}

              <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="eyebrow">Commande suivie</p>
                  <h3 className="font-display text-xl">{suivie.numero}</h3>
                  <p className="mt-1 text-sm text-muted">{suivie.modeLisible} · {suivie.creneau}</p>
                </div>
                <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', statutDe(suivie).ton)}>
                  {statutDe(suivie).libelle}
                </span>
              </div>

              <div className="mt-6 rounded-[22px] bg-surface p-5 shadow-card sm:p-6">
                <ol className="space-y-0">
                  {suivie.suivi.map((s, i) => {
                    const dernierFait = suivie.suivi.filter(x => x.fait).length - 1;
                    const actif = i === dernierFait + 1;
                    return (
                      <li key={s.titre} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-full border-2',
                            s.fait ? 'border-success bg-success text-white' : actif ? 'border-wine bg-wine-wash text-wine' : 'border-linen-deep text-linen-deep')}>
                            {s.fait ? <Icon name="check" size={16} /> : <span className="h-2 w-2 rounded-full bg-current" />}
                          </span>
                          {i < suivie.suivi.length - 1 && <span className={cn('my-1 w-0.5 flex-1', s.fait ? 'bg-success/40' : 'bg-linen-deep')} />}
                        </div>
                        <div className={cn('pb-6', !s.fait && !actif && 'opacity-55')}>
                          <p className={cn('text-sm font-semibold', s.fait || actif ? 'text-ink' : 'text-muted')}>{s.titre}</p>
                          <p className="mt-0.5 text-xs text-muted">{s.date}</p>
                          {actif && (
                            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-wine-wash px-2.5 py-1 text-xs font-medium text-wine">
                              <Icon name="clock" size={12} /> En cours
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>

                <div className="mt-2 flex flex-col gap-2 rounded-2xl bg-linen p-4">
                  <p className="flex items-start gap-2 text-sm text-ink-soft">
                    <Icon name="info" size={16} className="mt-0.5 shrink-0 text-wine" />
                    Vous serez prévenu par SMS à la pesée. Le solde de {eur(suivie.solde)} se règle au retrait, après annonce du poids réel.
                  </p>
                  <Link to={`/mon-compte/commandes/${suivie.numero}`}
                    className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-wine underline underline-offset-4">
                    Voir le détail et la pesée <Icon name="arrowRight" size={14} />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ------------------------------------------------------- FAVORIS */}
          {vue === 'favoris' && (
            <div>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl">Mes favoris</h2>
                  <p className="mt-1 text-sm text-muted">
                    Le cœur sur une fiche la garde ici, sur cet appareil. Rien n’est partagé.
                  </p>
                </div>
                <Link to="/boutique" className="text-sm font-semibold underline underline-offset-4">Le comptoir</Link>
              </div>

              {favorisProduits.length > 0 ? (
                /* Vue **liste** : une photo à gauche, le prix et les actions à
                   droite. Les favoris sont peu nombreux et se comparent mal en
                   grille de vignettes ; l'orientation paysage donne à chacun le
                   poids d'une ligne. Mêmes règles que `/boutique?vue=liste` —
                   `[data-vue='liste']` dans `app.css` — aucune variante de
                   composant. */
                <div data-vue="liste" className="mt-5 grid grid-cols-1 gap-3">
                  {favorisProduits.map(p => <ProductCard key={p.id} produit={p} />)}
                </div>
              ) : (
                <div className="mt-5 rounded-[20px] bg-surface p-6 text-center shadow-soft">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-linen text-wine">
                    <Icon name="heart" size={22} />
                  </div>
                  <p className="mt-3 font-display text-lg">Aucun favori pour l’instant</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-muted">
                    Touchez le cœur sur une pièce : elle vous attendra ici, avec son prix au kilo du jour.
                  </p>
                  <Link to="/boutique" className={clsBouton('primary', 'mt-4')}>Parcourir le comptoir</Link>
                </div>
              )}
            </div>
          )}

          {/* ----------------------------------------------------- ADRESSES */}
          {vue === 'adresses' && (
            <div>
              <div className="flex items-end justify-between">
                <h2 className="font-display text-2xl">Mes adresses</h2>
                <Bouton variante="quiet" taille="sm" icone="plus">Ajouter</Bouton>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {COMPTE.adresses.map(a => (
                  <article key={a.id} className="rounded-[20px] bg-surface p-5 shadow-soft">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">{a.libelle}</p>
                      {a.defaut && <span className="tag tag--success">Par défaut</span>}
                    </div>
                    <p className="mt-2 text-sm text-ink-soft">{a.ligne}<br />{a.complement && <>{a.complement}<br /></>}{a.codePostal} {a.ville}</p>
                    <div className="mt-3 flex gap-3 text-sm">
                      <button type="button" className="font-medium text-wine underline underline-offset-4">Modifier</button>
                      {!a.defaut && <button type="button" className="font-medium text-muted underline underline-offset-4">Définir par défaut</button>}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* -------------------------------------------------- PRÉFÉRENCES */}
          {vue === 'preferences' && (
            <div className="space-y-4">
              <h2 className="font-display text-2xl">Préférences</h2>

              <div className="rounded-[20px] bg-surface p-5 shadow-soft">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold">Confort de lecture</p>
                    <p className="mt-1 text-sm text-muted">Agrandit tous les textes du site de {REGLAGES.confort.echelle} %, sans casser la mise en page.</p>
                  </div>
                  <button type="button" role="switch" aria-checked={confort} onClick={basculerConfort}
                    className={cn('relative h-7 w-12 shrink-0 rounded-full transition', confort ? 'bg-wine' : 'bg-linen-deep')}>
                    <span className={cn('absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all', confort ? 'left-6' : 'left-1')} />
                    <span className="sr-only">Confort de lecture</span>
                  </button>
                </div>
              </div>

              <div className="rounded-[20px] bg-surface p-5 shadow-soft">
                <p className="font-semibold">Mode de service habituel</p>
                <p className="mt-1 text-sm text-muted">Il sera proposé par défaut à votre prochaine commande.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[{ v: 'PICKUP', l: 'Retrait', i: 'store' as const }, { v: 'DELIVERY', l: 'Livraison', i: 'truck' as const }].map(o => (
                    <span key={o.v} className={cn('inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm',
                      COMPTE.preferences.mode === o.v ? 'bg-ink text-lin-50' : 'bg-linen text-muted')}>
                      <Icon name={o.i} size={15} /> {o.l}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-[20px] bg-surface p-5 shadow-soft">
                <p className="font-semibold">Nous prévenir</p>
                <ul className="mt-3 space-y-2 text-sm text-ink-soft">
                  {['SMS à la pesée', 'E-mail de confirmation', 'Nouvelles pièces et arrivages'].map(t => (
                    <li key={t} className="flex items-center justify-between border-b border-linen-deep/60 pb-2 last:border-0">
                      {t}
                      <span className="text-xs font-semibold text-success">Activé</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-[20px] border border-danger/20 bg-danger-wash/40 p-5">
                <p className="font-semibold text-danger">Vos données</p>
                <p className="mt-1 text-sm text-ink-soft">
                  Compte et historique effacés {REGLAGES.rgpd.suppressionJours} jours après votre demande,
                  conformément au RGPD. Vos commandes en cours ne sont pas concernées avant leur clôture.
                </p>
                <button type="button" className="mt-3 text-sm font-semibold text-danger underline underline-offset-4">Demander la suppression de mon compte</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Info({ icone, titre, valeur }: { icone: IconName; titre: string; valeur: string }) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs text-muted"><Icon name={icone} size={14} /> {titre}</p>
      <p className="mt-1 text-sm font-medium capitalize">{valeur}</p>
    </div>
  );
}


/* ============================================================ DÉTAIL COMMANDE
 * L'écran qui **prouve** la promesse de pesée : ligne par ligne, ce qui était
 * estimé, ce qui a été pesé, l'écart en pourcentage et le prix final.
 * Tableau réel (`<caption>`, `<th scope>`) : il se lit aussi au lecteur d'écran.
 */
function DetailCommande({ c, onCommander }: { c: Commande; onCommander: () => void }) {
  const boutique = BOUTIQUES.find(b => b.id === c.boutiqueId);
  const paye = c.paye || c.arrhes;
  const reste = c.solde;
  const totalFinal = c.totalFinal ?? c.total;
  const aDesPesees = c.lignes.some(l => l.pese);

  return (
    <section aria-labelledby={`commande-${c.numero}`} className="rounded-[22px] bg-surface p-5 shadow-card sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Commande</p>
          <h3 id={`commande-${c.numero}`} className="mt-1 font-display text-2xl">{c.numero}</h3>
          <p className="mt-1 text-sm text-muted">{c.modeLisible} · commande du {c.date}</p>
          <p className="mt-0.5 text-sm text-ink-soft">{c.creneau}</p>
          {boutique && c.mode === 'PICKUP' && (
            <p className="mt-0.5 text-sm text-muted">{boutique.nom} — {boutique.adresse}</p>
          )}
        </div>
        <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', statutDe(c).ton)}>
          {statutDe(c).libelle}
        </span>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-left text-sm">
          <caption className="mb-2 text-left text-xs text-muted">
            Détail de la pesée — {aDesPesees ? 'poids réel et prix définitif' : 'poids estimé, pesée à venir'}
          </caption>
          <thead>
            <tr className="border-b border-linen-deep/70 text-[0.6875rem] uppercase tracking-[0.1em] text-muted">
              <th scope="col" className="py-2 pr-3 font-semibold">Produit</th>
              <th scope="col" className="py-2 pr-3 text-right font-semibold">Estimé</th>
              <th scope="col" className="py-2 pr-3 text-right font-semibold">Réel</th>
              <th scope="col" className="py-2 pr-3 text-right font-semibold">Écart</th>
              <th scope="col" className="py-2 pr-3 text-right font-semibold">Prix/kg</th>
              <th scope="col" className="py-2 text-right font-semibold">Prix final</th>
            </tr>
          </thead>
          <tbody>
            {c.lignes.map((l, i) => (
              <tr key={`${l.nom}-${i}`} className="border-b border-linen-deep/40 last:border-0">
                <th scope="row" className="py-2.5 pr-3 font-medium">
                  {l.nom}
                  {l.options && <span className="block text-[0.75rem] font-normal text-muted">{l.options}</span>}
                </th>
                <td className="py-2.5 pr-3 text-right tabular-nums text-ink-soft">
                  {l.poidsEstimeG ? kg(l.poidsEstimeG) : '—'}
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums">
                  {l.pese ? kg(l.poidsReelG ?? 0) : <span className="text-muted">en attente</span>}
                </td>
                <td className={cn('py-2.5 pr-3 text-right tabular-nums',
                  l.ecartPct == null ? 'text-muted' : l.ecartPct > 0 ? 'text-alert' : 'text-success')}>
                  {l.ecartPct == null ? '—' : `${l.ecartPct > 0 ? '+' : ''}${l.ecartPct.toFixed(1).replace('.', ',')} %`}
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-ink-soft">
                  {l.prixKg ? `${eur(l.prixKg)}` : '—'}
                </td>
                <td className="py-2.5 text-right font-semibold tabular-nums">
                  {l.prixFinal ? eur(l.prixFinal) : <>≈ {eur(l.prix)}</>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-linen px-4 py-3">
          <p className="text-[0.6875rem] uppercase tracking-[0.12em] text-muted">Payé en ligne</p>
          <p className="mt-0.5 font-display text-lg tabular-nums">{eur(paye)}</p>
        </div>
        <div className="rounded-xl bg-brass-wash/70 px-4 py-3">
          <p className="text-[0.6875rem] uppercase tracking-[0.12em] text-brass-ink/80">Reste à régler</p>
          <p className="mt-0.5 font-display text-lg tabular-nums text-brass-ink">{eur(reste)}</p>
        </div>
        <div className="rounded-xl bg-linen px-4 py-3">
          <p className="text-[0.6875rem] uppercase tracking-[0.12em] text-muted">Total {aDesPesees ? 'définitif' : 'estimé'}</p>
          <p className="mt-0.5 font-display text-lg tabular-nums">{eur(totalFinal)}</p>
        </div>
      </div>

      {!aDesPesees && (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-info-wash/60 px-4 py-3 text-[0.8125rem] text-info">
          <Icon name="info" size={15} className="mt-0.5 shrink-0" />
          En attente de pesée : vous recevrez le poids réel et le solde exact par SMS, avant le retrait.
        </p>
      )}

      {c.statut === 'NON_RETIREE' && (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-danger-wash/60 px-4 py-3 text-[0.8125rem] text-danger">
          <Icon name="info" size={15} className="mt-0.5 shrink-0" />
          Pièce non retirée : les arrhes restent acquises, conformément aux conditions de vente.
          Le comptoir peut, au cas par cas, proposer une autre issue — appelez-le.
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <Bouton variante="outline" taille="sm" icone="package" onClick={onCommander}>Recommander</Bouton>
        <button type="button" onClick={() => window.print()}
          className="btn btn--quiet btn--sm">
          <Icon name="download" size={15} /> Imprimer la facture (PDF)
        </button>
        <a href={`mailto:${REGLAGES.marque.email}?subject=${encodeURIComponent(`Problème — ${c.numero}`)}&body=${encodeURIComponent(`Bonjour,

Je souhaite signaler un problème sur la commande ${c.numero}.

`)}`}
          className="btn btn--ghost btn--sm">
          <Icon name="info" size={15} /> Signaler un problème
        </a>
      </div>
      <p className="mt-2 text-[0.75rem] text-muted">
        La facture s’imprime depuis cette page ; le PDF est aussi joint à l’e-mail de confirmation.
      </p>
    </section>
  );
}
